import type { ICohortQueryModel } from "@performyard/models";
import type { CreateReviewCycleRequestInput } from "@performyard/service-contracts";
import { convertToZonedUTCTime } from "@performyard/utils-isomorphic";

import { computeRelativeDueDate } from "../../../../../../components/FormSelectionCard/utils/relativeDueDate";

import type { AddFormsStepData, ConfiguredForm } from "../AddFormsStep/types";

import { normalizeAutomationEndDate } from "../../../../utils/automation-end-date";

import type { CycleType } from "../../Config";
import type { AutomationType } from "../CycleSettingsStep/constants";

type SettingsData = {
	cycleName?: string;
	startDate?: Date | null;
	adminsOnly?: boolean;
	lockReviews?: boolean;
	automationType?: AutomationType;
	automationEndDate?: Date | null;
	group?: { id: string; name: string } | null;
};

const WHO_CAN_EASY_ADD_REVERSE: Record<string, "admin" | "any-employee" | "manager-chain-of-command" | "subject"> = {
	"Any Employee": "any-employee",
	Subject: "subject",
	Manager: "manager-chain-of-command",
	Admin: "admin",
};

const APPROVAL_REVERSE: Record<string, "managers-and-admins" | "admins-only" | null> = {
	"No Approval Needed": null,
	"Require Approval from Admins": "admins-only",
	"Require Approval from Admins or Managers": "managers-and-admins",
};

const resolveAbsoluteDate = (
	value: Date | number | undefined,
	startDate: Date,
	isRelative: boolean,
	companyTimezone: string,
	defaultDueTime: number
): Date | undefined => {
	if (value === undefined) return undefined;
	// A static (absolute) due date is picked as a company-timezone wall-clock value,
	// so reinterpret it in the company timezone before sending to the API.
	if (value instanceof Date) return convertToZonedUTCTime(value, companyTimezone);
	// Relative due dates: add the days and then set the time to the company default cycle due time, in the company
	// timezone. Uses the same helper as the preview banner so the saved dates match what was shown.
	if (isRelative && typeof value === "number")
		return computeRelativeDueDate({ startDate, daysToAdd: value, defaultDueTime, companyTimezone });
	return undefined;
};

export type FormAssignmentInput = NonNullable<CreateReviewCycleRequestInput["formAssignments"]>[number];

const buildStandardAssignment = (
	form: ConfiguredForm<"standard">,
	startDate: Date,
	companyTimezone: string,
	defaultDueTime: number
): FormAssignmentInput | null => {
	const { data } = form;
	const isRelative = data.isRelativeDates;
	const authorsDueDate = resolveAbsoluteDate(
		data.authorsDueDate,
		startDate,
		isRelative,
		companyTimezone,
		defaultDueTime
	);
	if (!authorsDueDate) return null;

	// When relative, signersDueDate is days from authorsDueDate (matches the
	// "Days from Authors Due Date" label and generate-form-assignments.ts).
	const signersDueDate =
		data.formSigners.length > 0
			? resolveAbsoluteDate(data.signersDueDate, authorsDueDate, isRelative, companyTimezone, defaultDueTime)
			: undefined;

	return {
		basic: true,
		formBaseId: form.formId,
		redacted: data.redactAuthors,
		visible: data.makeVisibleWhenCompleted,
		authors: data.formAuthors.map((a) => a.id),
		authorsDueDate,
		signers: data.formSigners.length > 0 ? data.formSigners.map((s) => s.id) : undefined,
		signersDueDate,
	};
};

export const build360Assignment = (
	form: ConfiguredForm<"360">,
	companyTimezone: string
): FormAssignmentInput | null => {
	const { data } = form;
	const whoCanEasyAdd = WHO_CAN_EASY_ADD_REVERSE[data.whoCanAddForm];
	if (!whoCanEasyAdd) return null;

	const subjectChooseAuthor: "any-employee" | "self" | undefined =
		data.whoCanAddForm === "Subject" ? (data.formAuthor === "Any Employee" ? "any-employee" : "self") : undefined;

	const requiresApproval = APPROVAL_REVERSE[data.approval] ?? null;

	// 360 forms switch between relative/static purely via dueDatesToggle (the card converts the value
	// type on toggle and never touches isRelativeDates), so it alone drives the payload shape (PY-13509).
	const useStaticDates = data.dueDatesToggle;
	const authorsDueDateRaw = data.authorsDueDate;
	const signersDueDateRaw = data.signersDueDate;

	return {
		easyAdd: true,
		formBaseId: form.formId,
		redacted: data.redactAuthors,
		visible: data.makeVisibleWhenFinished,
		whoCanEasyAdd,
		subjectEmployeeChooseAuthor: subjectChooseAuthor,
		allowComments: data.allow360RequestMessage,
		isActive: !data.makeFormInactive,
		requiresApproval,
		...(useStaticDates
			? {
					authorsDueDate:
						authorsDueDateRaw instanceof Date
							? convertToZonedUTCTime(authorsDueDateRaw, companyTimezone)
							: undefined,
					// Only emit a signer due date when signers are actually selected — the backend
					// EasyAddFormConfigInput refinement requires signers and signer due dates to be
					// provided together (matches the relative branch's gating below).
					signersDueDate:
						data.formSigners.length > 0 && signersDueDateRaw instanceof Date
							? convertToZonedUTCTime(signersDueDateRaw, companyTimezone)
							: undefined,
				}
			: {
					daysToCompleteAuthors: typeof authorsDueDateRaw === "number" ? authorsDueDateRaw : undefined,
					// The card's field is days from the authors due date; easy-add configs store the total
					// from form-added. Clamp typed negatives so they can't sum into signers-before-authors.
					daysToCompleteSigners:
						data.formSigners.length > 0 &&
						typeof signersDueDateRaw === "number" &&
						typeof authorsDueDateRaw === "number"
							? Math.max(0, signersDueDateRaw) + authorsDueDateRaw
							: undefined,
				}),
		signers: data.formSigners.length > 0 ? data.formSigners.map((s) => s.id) : undefined,
	};
};

export const buildExternalAssignment = (form: ConfiguredForm<"external">): FormAssignmentInput => {
	const { data } = form;
	const hasReviewer = data.reviewerName || data.reviewerEmail || data.emailMessage;
	return {
		external: true,
		formBaseId: form.formId,
		redacted: data.redactAuthors,
		visible: data.makeVisibleWhenFinished,
		authors: hasReviewer
			? [
					{
						name: data.reviewerName,
						email: data.reviewerEmail,
						emailBody: data.emailMessage,
					},
				]
			: undefined,
	};
};

export const buildFormAssignments = (
	forms: AddFormsStepData["forms"],
	startDate: Date,
	companyTimezone: string,
	defaultDueTime: number
): FormAssignmentInput[] => {
	const assignments: FormAssignmentInput[] = [];
	for (const f of forms.standard) {
		const a = buildStandardAssignment(f, startDate, companyTimezone, defaultDueTime);
		if (a) assignments.push(a);
	}
	for (const f of forms["360"]) {
		const a = build360Assignment(f, companyTimezone);
		if (a) assignments.push(a);
	}
	for (const f of forms.external) {
		assignments.push(buildExternalAssignment(f));
	}
	return assignments;
};

export type BuildPayloadArgs = {
	cycleType: CycleType;
	settings: SettingsData;
	formsData: AddFormsStepData;
	userSelectionId: string | null;
	autoflowUserQuery: ICohortQueryModel | null;
	companyTimezone: string;
	defaultDueTime: number;
};

export const buildCreateCyclePayload = ({
	cycleType,
	settings,
	formsData,
	userSelectionId,
	autoflowUserQuery,
	companyTimezone,
	defaultDueTime,
}: BuildPayloadArgs): CreateReviewCycleRequestInput | null => {
	const workflowId = formsData.selectedTemplate?.id;
	if (!workflowId || !settings.startDate || !settings.cycleName) return null;

	// The date picker yields a Date with wall-clock time as picked by the user,
	// but this is intended to be relative to the company timezone.
	// If we convert to UTC now, it will represent the wall-clock of the acting user's browser TZ,
	// instead, we need to explicitly convert the Date to the company timezone.
	const startDate = convertToZonedUTCTime(settings.startDate, companyTimezone);

	const formAssignments = buildFormAssignments(formsData.forms, startDate, companyTimezone, defaultDueTime);
	if (formAssignments.length === 0) return null;

	const adminsOnly = settings.lockReviews ?? false;

	if (cycleType === "automated") {
		if (!autoflowUserQuery) return null;
		const autoflowType = settings.automationType === "recurring" ? "rolling" : "one-time";
		const endDate =
			autoflowType === "rolling" && settings.automationEndDate
				? normalizeAutomationEndDate(settings.automationEndDate, companyTimezone)
				: null;
		return {
			type: "automated",
			name: settings.cycleName,
			workflowId,
			startDate,
			formAssignments,
			adminsOnly,
			autoflow: { type: autoflowType, endDate, userQuery: autoflowUserQuery },
		};
	}

	if (!userSelectionId) return null;

	if (cycleType === "group") {
		if (!settings.group?.id) return null;
		return {
			type: "group",
			name: settings.cycleName,
			workflowId,
			startDate,
			userSelectionId,
			formAssignments,
			adminsOnly,
			departmentId: settings.group.id,
		};
	}

	return {
		type: "manual",
		name: settings.cycleName,
		workflowId,
		startDate,
		userSelectionId,
		formAssignments,
		adminsOnly,
	};
};
